import React, {useMemo} from 'react';
import {Card, MiniAreaChart, SectionTitle, Stat} from './primitives';
import {useSmartTestingMetrics} from '../hooks/useSmartTestingMetrics';
import type {SmartTestingDailyPoint} from '../services/smartTestingReportingService';

interface Props {
    /** MLD-side organization id; pulls Smart Testing metrics scoped to this org. */
    mldOrganizationId?: number;
    /** Optional fixed window — caller can pass campaign dates. Defaults to BE's "last 30 days". */
    startDate?: string;
    endDate?: string;
}

/**
 * Smart Testing card on the OHD employer dashboard. Sources from
 * storefront_order rows imported by the WooCommerce smart-testing
 * pipeline. Falls back to the original "Data Pending" placeholder
 * when no organization is in scope or no orders have landed yet, so
 * the card stays in the layout for orgs that haven't onboarded.
 */
const SmartTestingCard: React.FC<Props> = ({mldOrganizationId, startDate, endDate}) => {
    const query = useSmartTestingMetrics(mldOrganizationId, {startDate, endDate});
    const m = query.data;

    // The chart shows the last 5 days of sales. We pad with zeros if
    // fewer than 5 days have any orders, so the chart is never empty
    // when there's at least one order in the window.
    const chartData = useMemo(() => buildChartSeries(m?.daily_series ?? [], 5, m?.window_end), [m]);

    const hasData = !!m && m.orders > 0;

    if (!mldOrganizationId || query.isLoading || !hasData) {
        return <PlaceholderCard />;
    }

    return (
        <div>
            <SectionTitle className="mb-2">Smart Testing</SectionTitle>
            <Card>
                <div className="grid gap-0" style={{gridTemplateColumns: '1fr 2.3fr 2fr'}}>
                    <div className="flex flex-col justify-center space-y-4 border-r border-mld-panel-border p-5">
                        <Stat value={String(m.enrolled_participants)} label="Enrolled Participants" />
                        <Stat value={String(m.labs_ordered)} label="Labs Ordered" />
                    </div>
                    <div className="flex flex-col p-5">
                        <div className="mb-3 flex items-center justify-between">
                            <span className="text-[14px] font-semibold text-mld-text-primary">Sales</span>
                            <span className="text-[12px] text-mld-muted-text">Last 5 days</span>
                        </div>
                        <div className="flex-1">
                            <MiniAreaChart
                                data={chartData}
                                height={200}
                                yFormatter={(v) => `$${Math.round(Number(v))}`}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col py-5 pr-4">
                        <div className="mb-3 px-5 text-[14px] font-semibold text-mld-text-primary">Labs Ordered</div>
                        <div className="px-5 text-[12px] text-mld-muted-text leading-relaxed">
                            {m.labs_ordered} lab{m.labs_ordered === 1 ? '' : 's'} across {m.orders} order
                            {m.orders === 1 ? '' : 's'}.
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const PlaceholderCard: React.FC = () => (
    <div>
        <SectionTitle badge="Data Pending" className="mb-2">
            Smart Testing
        </SectionTitle>
        <Card>
            <div className="grid gap-0 opacity-60" style={{gridTemplateColumns: '1fr 2.3fr 2fr'}}>
                <div className="flex flex-col justify-center space-y-4 border-r border-mld-panel-border p-5">
                    <Stat value="—" label="Enrolled Participants" />
                    <Stat value="—" label="Labs Ordered" />
                </div>
                <div className="flex flex-col p-5">
                    <div className="mb-3 flex items-center justify-between">
                        <span className="text-[14px] font-semibold text-mld-text-primary">Sales</span>
                        <span className="text-[12px] text-mld-muted-text">Data pending</span>
                    </div>
                    <div className="flex-1">
                        <MiniAreaChart
                            data={[0, 0, 0, 0, 0].map((v, i) => ({day: `Day ${i + 1}`, value: v}))}
                            height={200}
                            yFormatter={(v) => `$${v}`}
                        />
                    </div>
                </div>
                <div className="flex flex-col py-5 pr-4">
                    <div className="mb-3 px-5 text-[14px] font-semibold text-mld-text-primary">Labs Ordered</div>
                    <div className="px-5 text-[12px] text-mld-muted-text">No orders in window yet.</div>
                </div>
            </div>
        </Card>
    </div>
);

/**
 * Take the most recent `count` days from a daily-series, padding with
 * zero-valued days if fewer points exist. The chart stays visually
 * stable (always shows N bars) even on a sparse window.
 */
function buildChartSeries(
    series: SmartTestingDailyPoint[],
    count: number,
    windowEndIso: string | undefined
): {day: string; value: number}[] {
    const endDate = windowEndIso ? new Date(windowEndIso) : new Date();
    const days: {day: string; value: number}[] = [];
    const byDay = new Map(series.map((p) => [p.day, p.sales_cents / 100]));
    for (let i = count - 1; i >= 0; i--) {
        const d = new Date(endDate);
        d.setUTCDate(d.getUTCDate() - i);
        const key = d.toISOString().slice(0, 10);
        days.push({day: key.slice(5), value: byDay.get(key) ?? 0}); // MM-DD label
    }
    return days;
}

export default SmartTestingCard;
